import {
  ALGOLIA_HAS_LISTABLE_STOCK_FILTER,
  ALGOLIA_INDEX_NAME,
  ALGOLIA_INDEX_TO_SORT_ORDER,
  DEFAULT_SORT_ORDER,
  NUMBER_OF_PRODUCTS_TO_SHOW,
  SORT_BY_OPTIONS,
} from 'commons/constants';
import { buildNewArrivalsGoliveDateNumericFilter } from './buildNewArrivalsGoliveDateFilter';
import CLEARANCE_PAGE_SEO from './clearancePageSeo';

/**
 * Page types for the Algolia-backed category page family (category, collection,
 * clearance, flash sale, ready-to-ship, new arrivals).
 *
 * Each entry is the single config point for its page: filter clause, default
 * sort, query shape, Alexandria sourcing, widgets and URL params.
 *
 * Unrelated to the Avo analytics `page_type` / `Location` values returned by
 * `useUserCurrentPageType` — do not mix the two enums.
 *
 * `variantWarehouseField` is the field name inside a `product_variants` entry in
 * the `products_jb4` index that holds the warehouse ids for that page type, e.g.
 * `product_variants.in_stock_warehouseIds:154`. Consumers that expand warehouse
 * membership client-side read the same field, so this is the single source of
 * truth for both.
 *
 * The contract is three parallel per-variant arrays —
 * `in_stock_warehouseIds`, `flash_sale_warehouseIds`, `clearance_warehouseIds`.
 * All three are warehouse-scoped: there is no global per-variant sale flag, so
 * never emit a clause on a bare signal. None is indexed on the dev app yet, so
 * these pages render empty locally until Caravel ships them.
 */
export const PAGE_TYPE = {
  CATEGORY: 'category',
  COLLECTION: 'collection',
  CLEARANCE: 'clearance',
  FLASH_SALE: 'flashSale',
  READY_TO_SHIP: 'readyToShip',
  NEW_ARRIVALS: 'newArrivals',
};

/** URL params owned by the page itself, in the order they are written. */
export const URL_PARAM = {
  PAGES: 'pages',
  SORT_BY: 'sortBy',
  QUICKSHIP: 'quickship',
  WIDTH_RANGE: 'widthRange',
  SEARCH: 'search',
  COLOR: 'color',
};

// Every param this page family may write, regardless of page type. Clearing is
// keyed off this rather than a page type's own `urlParams`: a param carried in
// from another page type has no widget to clear it, so scoping the clear to the
// owned params would leave it stuck in the URL forever.
export const ALL_URL_PARAMS = Object.values(URL_PARAM);

// Params backed by a hardcoded widget rather than a dynamic Alexandria facet.
// Deliberately not derived from the `show*` flags: hiding the widget does not
// stop Alexandria returning a filter with the same id, and that filter would
// then render as a generic text facet and write an unindexed attribute (e.g.
// `color`, indexed as `option_color`) into the Algolia filter string.
const WIDGET_FILTER_IDS = new Set(
  ALL_URL_PARAMS.filter(
    param => param !== URL_PARAM.PAGES && param !== URL_PARAM.SORT_BY
  )
);

/**
 * Where a page type gets its page metadata and sidebar facets.
 * - `page`: the Alexandria `GET_CATEGORY_PAGE` query.
 * - `descriptor`: no query; the descriptor's own `seo` block supplies metadata
 *   and the page renders no dynamic facet filters.
 */
export const DATA_SOURCE = {
  PAGE: 'page',
  DESCRIPTOR: 'descriptor',
};

export const BASE_ATTRIBUTES_TO_RETRIEVE = [
  '_highlightResult',
  'product_id',
  'name',
  'is_in_stock',
];

// The per-SKU array a SKU-specific page reads to decide which upholstery
// options each product card may show. Retrieved only for those page types — on
// a configurable product it is by far the largest field in the record, and a
// plain category page must not pay for it.
export const VARIANT_MATCHING_ATTRIBUTE = 'product_variants';

// The general per-variant stock signal, as opposed to a sale-scoped one — the
// field the ready-to-ship page is scoped by.
export const IN_STOCK_WAREHOUSE_FIELD = 'in_stock_warehouseIds';

// Nested retrieve for the zip-aware card badge. Algolia returns each variant
// with only this field, not the full `product_variants` object.
export const IN_STOCK_WAREHOUSE_RETRIEVE_ATTRIBUTE = `product_variants.${IN_STOCK_WAREHOUSE_FIELD}`;

/**
 * Zip-aware card badges need the per-SKU warehouse ids, not the whole
 * variant object. Only when the Ready-to-Ship toggle is off: with the toggle
 * on, the filter already proved membership, so the nested field is unused.
 * SKU-matching page types already retrieve `product_variants`. Returns
 * `attributes` unchanged when there is nothing to append, so callers can
 * keep the descriptor's frozen array as a `useMemo` dependency.
 */
export const appendInStockWarehouseAttributeIfZip = (
  attributes,
  hasUserZip,
  isReadyToShipActive = false
) => {
  if (
    !hasUserZip ||
    isReadyToShipActive ||
    attributes.includes(VARIANT_MATCHING_ATTRIBUTE) ||
    attributes.includes(IN_STOCK_WAREHOUSE_RETRIEVE_ATTRIBUTE)
  ) {
    return attributes;
  }
  return [...attributes, IN_STOCK_WAREHOUSE_RETRIEVE_ATTRIBUTE];
};

// Base constraints every listing query carries: the leading `filters` clause
// and the leading `numericFilters` entries that restrict the catalogue to
// listable, available, visible products. Descriptor fields rather than builder
// constants because a sale page overrides them (see CLEARANCE).
export const DEFAULT_BASE_FILTERS = [ALGOLIA_HAS_LISTABLE_STOCK_FILTER];
export const DEFAULT_BASE_NUMERIC_FILTERS = [
  'item_availability=0',
  'visibility>1',
  'visibility<4',
];

const DESCRIPTOR_DEFAULTS = {
  // Algolia filter clause
  // Written first, in this order: `baseFilters` leads the `filters` string and
  // `baseNumericFilters` leads `numericFilters`. Override only where page
  // membership is proven by another clause and the products it proves are
  // legitimately unlistable — never to widen a catalogue page.
  baseFilters: DEFAULT_BASE_FILTERS,
  baseNumericFilters: DEFAULT_BASE_NUMERIC_FILTERS,
  usesCategorySlugFilter: false,
  variantWarehouseField: null,
  // Called per build, not stored as data: a date-window clause has to advance
  // with the clock, and descriptors are frozen once at module load.
  extraNumericFilters: () => [],

  // Query shape
  defaultSortIndex: ALGOLIA_INDEX_NAME.PRODUCTS,
  // Which advanced-search section the page occupies (products vs reviews vs
  // photos). Not a sort index — a replica `defaultSortIndex` does not change it.
  searchSection: ALGOLIA_INDEX_NAME.PRODUCTS,
  sortOptions: SORT_BY_OPTIONS,
  hitsPerPage: NUMBER_OF_PRODUCTS_TO_SHOW,
  extraAttributesToRetrieve: [],
  // SKU-specific rendering (CON-8561, decision (b)): the page keeps one card
  // per product but shows only the upholstery variants whose SKUs qualify at
  // the user's warehouse, matched against the hit's `product_variants`. The
  // name predates the decision — it gates matched-upholstery filtering, not
  // one-card-per-SKU expansion.
  expandsVariants: false,
  // Zero-drop fallback for SKU-matching pages (CON-8567): when none of a hit's
  // qualifying SKUs has an enriched counterpart, keep the product with its
  // full, unrestricted variant list (approximate swatches, plain-card
  // quickship) instead of hiding it. Only correct where page membership is
  // product-true regardless of which swatch renders (ready-to-ship); on
  // clearance/flash-sale membership is SKU-specific and the fallback would
  // render regular-priced upholsteries.
  keepUnmatchedProducts: false,

  // Alexandria sourcing
  metadataSource: DATA_SOURCE.PAGE,
  facetSource: DATA_SOURCE.PAGE,
  // Whether the page may fall back to a client-side `GET_CATEGORY_PAGE`.
  allowsClientPageFetch: true,

  // Widgets. `urlParams` is derived from these, so turning a widget off also
  // drops its URL param and its filter clause.
  showReadyToShipToggle: true,
  showWidthFilter: true,
  showSearchBox: true,
  showColorFilter: true,
  showCollectionBackLink: false,
  showBestSellersCarousel: true,

  // Page identity for remount keys and the filter-reset effect. `null` means
  // "use the slug", which is all a slug-driven page needs; the legacy pages are
  // CMS components at fixed paths and need an explicit key.
  pageKey: null,

  // Static copy for page types with no Alexandria page behind them.
  seo: null,

  // Avo fallbacks for page types with no Alexandria `pageId` / `pageName`.
  analyticsListId: null,
  analyticsCategoryId: null,
  // Explicit opt-in to firing avo events with `categoryId: null` when neither
  // `pageId` nor `analyticsCategoryId` yields an int. Avo types `category_id`
  // as int (asserts are dev-only), so a page with no real id either ships null
  // — what the legacy instock page has always sent — or stays silent. Without
  // this flag the NaN gate suppresses every event on such a page.
  analyticsAllowNullCategoryId: false,
};

const getUrlParams = descriptor => [
  URL_PARAM.PAGES,
  URL_PARAM.SORT_BY,
  ...(descriptor.showReadyToShipToggle ? [URL_PARAM.QUICKSHIP] : []),
  ...(descriptor.showWidthFilter ? [URL_PARAM.WIDTH_RANGE] : []),
  ...(descriptor.showSearchBox ? [URL_PARAM.SEARCH] : []),
  ...(descriptor.showColorFilter ? [URL_PARAM.COLOR] : []),
];

// Descriptors are normalized once at module load so every consumer gets the
// same frozen object identity and can use it as a `useMemo` dependency.
const normalize = overrides => {
  const descriptor = { ...DESCRIPTOR_DEFAULTS, ...overrides };
  const urlParams = getUrlParams(descriptor);

  return Object.freeze({
    ...descriptor,
    urlParams,
    defaultSortOrder:
      ALGOLIA_INDEX_TO_SORT_ORDER[descriptor.defaultSortIndex] ??
      DEFAULT_SORT_ORDER,
    attributesToRetrieve: [
      ...BASE_ATTRIBUTES_TO_RETRIEVE,
      ...(descriptor.expandsVariants ? [VARIANT_MATCHING_ATTRIBUTE] : []),
      ...descriptor.extraAttributesToRetrieve,
    ],
    skipFilterIds: WIDGET_FILTER_IDS,
  });
};

export const CATEGORY_PAGE_TYPE_CONFIG = {
  [PAGE_TYPE.CATEGORY]: normalize({
    usesCategorySlugFilter: true,
  }),
  [PAGE_TYPE.COLLECTION]: normalize({
    usesCategorySlugFilter: true,
    showCollectionBackLink: true,
  }),
  // The `/clearance/` page (CON-8565). Fully descriptor-sourced like
  // ready-to-ship: Alexandria's `clearance` pseudo-category has `pageId: null`
  // and all-null metadata (the page name and SEO copy have always come from
  // the front end), and fetching it anyway would run the legacy `getClearance`
  // resolver — two live Caravel calls — for the sake of its sidebar facets.
  [PAGE_TYPE.CLEARANCE]: normalize({
    variantWarehouseField: 'clearance_warehouseIds',
    expandsVariants: true,
    // Clearance exists to sell discontinued units, and a discontinued product
    // fails every base constraint (`has_listable_stock: false`,
    // `item_availability: 2`, `visibility: 0` — e.g. 11455 Omari Wall Art,
    // which the legacy page lists at the Miami warehouse on dev). Membership
    // is fully proven by the warehouse clause, so the base constraints are
    // dropped for legacy parity instead of silently hiding those products.
    baseFilters: [],
    baseNumericFilters: [],
    // Membership is SKU-specific: a product is on the page because particular
    // SKUs are clearance units at the user's warehouse, so a zero-match
    // product must drop. `keepUnmatchedProducts` stays at its default `false`
    // on purpose — the fallback card would show regular-priced upholsteries.
    //
    // A clearance unit is in the warehouse by definition, so the toggle would
    // filter nothing; legacy deletes the quickship facet on this page too.
    showReadyToShipToggle: false,
    showBestSellersCarousel: false,
    metadataSource: DATA_SOURCE.DESCRIPTOR,
    facetSource: DATA_SOURCE.DESCRIPTOR,
    // Fixed-path CMS component — a slug-keyed page identity would never fire
    // the filter-reset effect. Doubles as the route path the consumer app's
    // `getCatchAllRouteKind` recognises the page by, so its `generateMetadata`
    // can serve the `seo` block below.
    pageKey: 'clearance',
    // The copy the legacy hook has always hardcoded, shared with it verbatim
    // (Alexandria field names; includes the rendered `pageLongDescription`).
    // Emitted once, by the catch-all route's `generateMetadata` into the raw
    // HTML; neither page adds a Helmet copy after hydration (CON-8801). The
    // legacy hook still reads this object for its visible copy and JSON-LD.
    seo: CLEARANCE_PAGE_SEO,
    // Exact payload parity with the legacy page: `productListViewed` /
    // `productListFiltered` fire with `categoryId: null` and `listId` /
    // `category` = 'Clearance' — the name came from the hook, never Alexandria.
    analyticsListId: 'Clearance',
    analyticsAllowNullCategoryId: true,
  }),
  // The `/flash-sale/` page (CON-8563). Fully descriptor-sourced: legacy
  // `getFlashsalePage` returns no metadata at all (null `pageName`, null
  // `pageHtmlTitle`, empty `filters`) and calls Caravel live, so querying it
  // would cost a round trip to learn nothing.
  [PAGE_TYPE.FLASH_SALE]: normalize({
    variantWarehouseField: 'flash_sale_warehouseIds',
    expandsVariants: true,
    // Deliberately not `keepUnmatchedProducts`: membership is SKU-specific, so
    // a fallback card would show regular-priced upholsteries on a sale page.
    metadataSource: DATA_SOURCE.DESCRIPTOR,
    facetSource: DATA_SOURCE.DESCRIPTOR,
    // Fixed-path CMS component — a slug-keyed page identity would never fire
    // the filter-reset effect. Doubles as the route path the consumer app's
    // `getCatchAllRouteKind` recognises the page by, so its `generateMetadata`
    // can serve the `seo` block below.
    pageKey: 'flash-sale',
    // A flash-sale unit is stocked by definition, so the toggle filters
    // nothing.
    showReadyToShipToggle: false,
    showBestSellersCarousel: false,
    seo: {
      // Alexandria field names — this block stands in for `getCategoryPage`.
      // All three are new copy: the legacy resolver returns null for every SEO
      // field and the page rendered the route title "flash-sale | Joybird".
      // Emitted once, by the catch-all route's `generateMetadata` into the raw
      // HTML; neither page adds a Helmet copy after hydration (CON-8801).
      // useFlashsalePageData still merges `pageHtmlTitle` /
      // `pageMetaDescription` into the legacy page data (unread while the
      // server owns the tags);
      // `pageName` stays off the legacy page because it also drives its
      // heading and Avo list id.
      pageName: 'Flash Sale',
      pageHtmlTitle: 'Flash Sale Furniture | Joybird',
      pageMetaDescription:
        'Shop the Joybird flash sale for limited-time savings on handcrafted sofas, sectionals, chairs, and more. Ready to ship from a warehouse near you, while supplies last.',
    },
    // Legacy sends `listId: null` (its `pageName` is null) and the null-name
    // guard suppresses `productListFiltered` entirely. Sending a real list id
    // is a deliberate, non-parity improvement; the null opt-in stays because
    // there is still no Alexandria `pageId` behind the page.
    analyticsListId: 'Flash Sale',
    analyticsAllowNullCategoryId: true,
  }),
  // The `/instock/` page (CON-8567). Fully descriptor-sourced: Alexandria's
  // `'instock'` pseudo-category has `pageId: null` and fabricates its metadata
  // from constants at read time, and its `filters` is always `[]` — so there
  // is nothing to fetch, and fetching anyway would fire the entire legacy
  // `getInstock` resolver including its live Caravel call.
  [PAGE_TYPE.READY_TO_SHIP]: normalize({
    variantWarehouseField: IN_STOCK_WAREHOUSE_FIELD,
    expandsVariants: true,
    // Membership ("some SKU of this product is stocked at the user's
    // warehouse") is product-true, so never hide a matched product over a
    // client-side enrichment miss.
    keepUnmatchedProducts: true,
    // The warehouse clause already is the stock filter, so the toggle would add
    // nothing.
    showReadyToShipToggle: false,
    // Explicit: the page has no Alexandria `pageId` for the widgets to query
    // by, so don't rely on the missing id to hide them.
    showBestSellersCarousel: false,
    metadataSource: DATA_SOURCE.DESCRIPTOR,
    facetSource: DATA_SOURCE.DESCRIPTOR,
    // Fixed-path CMS component — a slug-keyed page identity would never fire
    // the filter-reset effect. Doubles as the route path the consumer app's
    // `getCatchAllRouteKind` recognises the page by, so its `generateMetadata`
    // can serve the `seo` block below.
    pageKey: 'instock',
    seo: {
      // Alexandria field names — this block is spread as the
      // `getCategoryPage` stand-in. `pageName` and `pageMetaDescription`
      // duplicate alexandria-js's `READY_TO_SHIP_CATEGORY_LABEL` /
      // `QUICKSHIP_PAGE_SEO_DESCRIPTION` (commons the client bundle cannot
      // import); `pageHtmlTitle` is new copy — the legacy page used to ship an
      // empty <title>. Emitted once, by the catch-all route's
      // `generateMetadata` into the raw HTML; neither page adds a Helmet copy
      // after hydration (CON-8801).
      pageName: 'Ready to Ship',
      pageHtmlTitle: 'Ready to Ship Furniture | Joybird',
      pageMetaDescription:
        'Shop Joybird’s ready-to-ship furniture collection and skip the wait. Discover sofas, sectionals, chairs, recliners, beds, tables, and more in modern and MCM styles, delivered faster to your home',
    },
    // Exact payload parity with the legacy page (CON-8567 decision #3):
    // `productListViewed` / `productListFiltered` fire with `categoryId: null`
    // and `listId` / `category` = 'Ready to Ship'.
    analyticsListId: 'Ready to Ship',
    analyticsAllowNullCategoryId: true,
  }),
  // New arrivals is scoped by a go-live date window in `numericFilters`, not by
  // a warehouse or category clause, so it has no membership clause here.
  [PAGE_TYPE.NEW_ARRIVALS]: normalize({
    extraNumericFilters: nowMs => [
      buildNewArrivalsGoliveDateNumericFilter(nowMs),
    ],
    defaultSortIndex: ALGOLIA_INDEX_NAME.PRODUCTS_GOLIVE_DATE_DESC,
    // SSR supplies metadata and borrows the generic page's facets. The client
    // resolver would run getNewArrivals -> hgetall('products') (CON-8566).
    allowsClientPageFetch: false,
  }),
};

export const getCategoryPageTypeConfig = pageType =>
  CATEGORY_PAGE_TYPE_CONFIG[pageType] ||
  CATEGORY_PAGE_TYPE_CONFIG[PAGE_TYPE.CATEGORY];

/** Page identity: the descriptor's own key, or the slug for slug-driven pages. */
export const getCategoryPageKey = (pageType, slug) =>
  getCategoryPageTypeConfig(pageType).pageKey ?? slug;

export const NEW_ARRIVALS_SLUG = 'new-arrivals';

// New Arrivals is the only page type derivable from the slug alone; the
// warehouse-scoped types are chosen by the route that renders the page.
export const getPageTypeForSlug = slug =>
  slug === NEW_ARRIVALS_SLUG ? PAGE_TYPE.NEW_ARRIVALS : PAGE_TYPE.CATEGORY;
